import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const FOREST = "#163A2A";
const MINT = "#8FC69B";
const CREAM = "#FAF7F2";
const ERROR = "#B23A1F";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Waitlist() {
  const [form, setForm] = useState({ name: "", mobile: "", email: "" });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(null);

  useEffect(() => {
    fetch(`${API}/waitlist/count`)
      .then((r) => r.json())
      .then((d) => setCount(d.count))
      .catch(() => {});
  }, []);

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Please tell us your name";

    const hasMobile = !!form.mobile.trim();
    const hasEmail = !!form.email.trim();

    if (hasMobile && !/^[+\d][\d\s\-]{8,}$/.test(form.mobile.trim()))
      errs.mobile = "Enter a valid mobile number";
    if (hasEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      errs.email = "Enter a valid email address";

    if (!hasMobile && !hasEmail) {
      errs.contact = "Please add a mobile number or an email so we can reach you";
    }
    return errs;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: "" }));
    if (errors.contact && (name === "mobile" || name === "email"))
      setErrors((p) => ({ ...p, contact: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          mobile: form.mobile.trim() || null,
          email: form.email.trim() || null,
        }),
      });
      if (!res.ok) throw new Error("Request failed");
      setSubmitted(true);
      setCount((c) => (c == null ? c : c + 1));
    } catch (err) {
      setErrors((p) => ({ ...p, contact: "Something went wrong. Please try again." }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="waitlist-section" className="w-full">
      {/* Top half — green */}
      <div style={{ backgroundColor: FOREST }} className="px-6 sm:px-10 lg:px-16 pt-24 sm:pt-32 pb-16 sm:pb-20">
        <div className="max-w-xl mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-[11px] font-bold uppercase tracking-[0.28em] mb-6"
            style={{ color: MINT }}
          >
            Early Access · Limited First Run
          </motion.p>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.06 }}
            className="text-4xl sm:text-5xl lg:text-[3.4rem] leading-[1.08]"
            style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, color: CREAM, marginBottom: '1.725rem' }}
          >
            Be first at the table when we launch.
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.14 }}
            className="text-base sm:text-lg leading-relaxed"
            style={{ color: "#FAF7F2" }}
          >
            Join the waitlist for your everyday favorites — cleaner, healthier, and freshly <strong>FLAVRD.</strong>
          </motion.p>
        </div>
      </div>

      {/* Bottom half — white */}
      <div style={{ backgroundColor: "#F4FAF0" }} className="px-6 sm:px-10 lg:px-16 pt-16 sm:pt-20 pb-24 sm:pb-32">
        <div className="max-w-xl mx-auto">
          <AnimatePresence mode="wait">
            {submitted ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                data-testid="waitlist-success"
                className="py-4"
              >
                <p className="text-3xl mb-3" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, color: FOREST }}>
                  You're on the list!
                </p>
                <p className="text-base sm:text-lg" style={{ color: "#163A2A" }}>
                  We'll be in touch soon with exclusive early-access details from the kitchen.
                </p>
              </motion.div>
            ) : (
              <motion.form
                key="form"
                onSubmit={handleSubmit}
                noValidate
                data-testid="waitlist-form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex flex-col gap-8"
              >
                <Field delay={0.2} label="Name" badge="required" id="name" name="name" type="text" placeholder="What should we call you?" value={form.name} onChange={handleChange} error={errors.name} testId="waitlist-name-input" disclaimer="(We promise to reach out only when we launch our first batch)" />
                <Field delay={0.28} label="Mobile" badge="optional" id="mobile" name="mobile" type="tel" placeholder="+91 98xxx xxxxx" value={form.mobile} onChange={handleChange} error={errors.mobile} testId="waitlist-mobile-input" />
                <Field delay={0.36} label="Email" badge="optional" id="email" name="email" type="email" placeholder="you@kitchen.com" value={form.email} onChange={handleChange} error={errors.email} testId="waitlist-email-input" />

                {errors.contact && (
                  <p className="-mt-3 text-xs" data-testid="waitlist-contact-error" style={{ color: ERROR }}>
                    {errors.contact}
                  </p>
                )}

                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.44 }}
                  className="pt-2"
                >
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="waitlist-submit-button"
                    className="inline-flex items-center gap-2.5 px-8 py-4 rounded-full font-semibold text-sm tracking-wide transition-all duration-300 hover:opacity-90 active:scale-95 disabled:opacity-60"
                    style={{ backgroundColor: MINT, color: FOREST }}
                  >
                    {loading ? "Securing…" : "Secure Early Access →"}
                  </button>

                  {count != null && (
                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.5 }}
                      data-testid="waitlist-count"
                      className="mt-5 text-sm flex items-center gap-2"
                      style={{ color: "rgba(22,58,42,0.65)" }}
                    >
                      <span
                        className="inline-block w-2 h-2 rounded-full"
                        style={{ backgroundColor: MINT, boxShadow: `0 0 0 3px rgba(143,198,155,0.35)` }}
                      />
                      <span>
                        <strong style={{ color: FOREST }}>{count.toLocaleString()}</strong> people already on the list
                      </span>
                    </motion.p>
                  )}
                </motion.div>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

function Field({ label, badge, id, name, type, placeholder, value, onChange, error, testId, delay, disclaimer }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay }}
    >
      <div className="mb-3">
        <label htmlFor={id} className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: "#265632" }}>
          {label}
          {badge && (
            <span
              data-testid={`${testId}-badge`}
              className="text-[9px] font-semibold uppercase tracking-[0.12em] px-2 py-0.5 rounded-full"
              style={
                badge === "required"
                  ? { color: FOREST, backgroundColor: MINT }
                  : { color: "rgba(22,58,42,0.90)", border: "1px solid rgba(22,58,42,0.28)" }
              }
            >
              {badge}
            </span>
          )}
        </label>
        {disclaimer && (
          <span className="block mt-1 text-[11px]" style={{ color: "#265632" }}>
            {disclaimer}
          </span>
        )}
      </div>
      <input
        id={id}
        name={name}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        data-testid={testId}
        autoComplete="off"
        className="waitlist-input w-full bg-transparent text-base pb-3 transition-all duration-200 outline-none"
        style={{
          color: error ? ERROR : FOREST,
          borderBottom: error ? `1px solid ${ERROR}` : "1px solid rgba(22,58,42,0.25)",
          fontFamily: "'Inter', sans-serif",
          caretColor: FOREST,
        }}
        onFocus={(e) => { if (!error) e.target.style.borderBottomColor = FOREST; }}
        onBlur={(e) => { if (!error) e.target.style.borderBottomColor = "rgba(22,58,42,0.25)"; }}
      />
      {error && <p className="mt-1.5 text-xs" data-testid={`${testId}-error`} style={{ color: ERROR }}>{error}</p>}
    </motion.div>
  );
}
